import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import {
    downloadRecruitmentReportCsv,
    fetchRecruitmentReport,
    RecruitmentReportBucket,
    RecruitmentReportingBreakdown,
    RecruitmentReportingResponse,
} from '../api/recruitmentReportingApi';
import { extractErrorMessageSync } from '../utils/errorHandler';
import { defaultRecruitmentReportingQuery } from '../utils/recruitmentReportingDates';
import { formatAmsterdamCalendarDate } from '../utils/amsterdamCalendarDate';

function total(data: RecruitmentReportingResponse | null, key: 'registrations' | 'suitable' | 'offered' | 'placed') {
    return data?.timeline.reduce((sum, row) => sum + row[key], 0) ?? 0;
}

function placementStarts(data: RecruitmentReportingResponse | null): number {
    return data?.timeline.reduce((sum, row) => sum + (row.placementStarts ?? 0), 0) ?? 0;
}

function BreakdownTable({ title, rows }: { title: string; rows: RecruitmentReportingBreakdown[] }) {
    return (
        <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, borderRadius: 1 }}>
            <Typography variant="subtitle1" fontWeight={800} sx={{ px: 2, pt: 2 }}>{title}</Typography>
            <TableContainer sx={{ maxHeight: 360 }}>
                <Table size="small" stickyHeader>
                    <TableHead><TableRow><TableCell>Name</TableCell><TableCell align="right">New</TableCell><TableCell align="right">Placed</TableCell></TableRow></TableHead>
                    <TableBody>
                        {rows.map((row) => <TableRow key={row.key}><TableCell>{row.key || 'Unknown'}</TableCell><TableCell align="right">{row.registrations}</TableCell><TableCell align="right">{row.placed}</TableCell></TableRow>)}
                        {rows.length === 0 && <TableRow><TableCell colSpan={3}>No data for this period.</TableCell></TableRow>}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}

export default function RecruitmentReportingPage() {
    const [query, setQuery] = useState(defaultRecruitmentReportingQuery);
    const [data, setData] = useState<RecruitmentReportingResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const requestGeneration = useRef(0);

    const load = useCallback(async () => {
        const generation = ++requestGeneration.current;
        setLoading(true);
        setError(null);
        try {
            const response = await fetchRecruitmentReport(query);
            if (generation !== requestGeneration.current) return;
            setData(response);
        } catch (loadError) {
            if (generation !== requestGeneration.current) return;
            setError(extractErrorMessageSync(loadError));
        } finally {
            if (generation === requestGeneration.current) setLoading(false);
        }
    }, [query]);

    useEffect(() => {
        setData(null);
        void load();
        return () => {
            requestGeneration.current += 1;
        };
    }, [load]);

    const totals = useMemo(() => {
        const registrations = total(data, 'registrations');
        const placed = total(data, 'placed');
        return {
            registrations,
            suitable: total(data, 'suitable'),
            offered: total(data, 'offered'),
            placed,
            placementStarts: placementStarts(data),
            conversion: registrations ? Math.round((placed / registrations) * 1000) / 10 : 0,
        };
    }, [data]);

    const exportCsv = async () => {
        setDownloading(true);
        setError(null);
        try {
            await downloadRecruitmentReportCsv(query);
        } catch (downloadError) {
            setError(extractErrorMessageSync(downloadError));
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                    <Box>
                        <Typography variant="h4" fontWeight={800}>Recruitment reporting</Typography>
                        <Typography variant="body2" color="text.secondary">Candidate intake, suitability, offers, and placement conversion.</Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
                        <TextField label="From" type="date" size="small" value={query.from} InputLabelProps={{ shrink: true }} onChange={(event) => setQuery({ ...query, from: event.target.value })} />
                        <TextField label="To" type="date" size="small" value={query.to} InputLabelProps={{ shrink: true }} onChange={(event) => setQuery({ ...query, to: event.target.value })} />
                        <FormControl size="small" sx={{ minWidth: 120 }}><InputLabel id="recruitment-report-period-label">Period</InputLabel><Select id="recruitment-report-period" labelId="recruitment-report-period-label" value={query.bucket} label="Period" onChange={(event) => setQuery({ ...query, bucket: event.target.value as RecruitmentReportBucket })}><MenuItem value="week">Week</MenuItem><MenuItem value="month">Month</MenuItem></Select></FormControl>
                        <Button variant="outlined" startIcon={<RefreshOutlinedIcon />} onClick={() => void load()} disabled={loading}>Refresh</Button>
                        <Button variant="contained" startIcon={<DownloadOutlinedIcon />} onClick={() => void exportCsv()} disabled={loading || downloading}>CSV</Button>
                    </Stack>
                </Stack>
                {error && <Alert severity="error">{error}</Alert>}
                {data && <Alert severity="info">{data.conversionDefinition}</Alert>}
                {loading ? <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 260 }}><CircularProgress /></Box> : data ? (
                    <>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(6, 1fr)' }, gap: 1.5 }}>
                            {Object.entries({ 'New candidates': totals.registrations, Suitable: totals.suitable, Offered: totals.offered, 'Placement starts': totals.placementStarts, 'Cohort placed': totals.placed, Conversion: `${totals.conversion}%` }).map(([label, value]) => (
                                <Paper key={label} variant="outlined" sx={{ p: 2, borderRadius: 1 }}><Typography variant="caption" color="text.secondary">{label}</Typography><Typography variant="h4" fontWeight={800}>{value}</Typography></Paper>
                            ))}
                        </Box>
                        <Paper variant="outlined" sx={{ borderRadius: 1 }}>
                            <TableContainer><Table size="small"><TableHead><TableRow><TableCell>Period</TableCell><TableCell align="right">New</TableCell><TableCell align="right">Suitable</TableCell><TableCell align="right">Offered</TableCell><TableCell align="right">Placement starts</TableCell><TableCell align="right">Cohort placed</TableCell><TableCell align="right">Conversion</TableCell></TableRow></TableHead><TableBody>{data.timeline.map((row) => <TableRow key={row.periodStart}><TableCell>{formatAmsterdamCalendarDate(row.periodStart)}</TableCell><TableCell align="right">{row.registrations}</TableCell><TableCell align="right">{row.suitable}</TableCell><TableCell align="right">{row.offered}</TableCell><TableCell align="right">{row.placementStarts ?? 0}</TableCell><TableCell align="right">{row.placed}</TableCell><TableCell align="right">{Math.round(row.registrationToPlacementConversion * 1000) / 10}%</TableCell></TableRow>)}</TableBody></Table></TableContainer>
                        </Paper>
                        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}><BreakdownTable title="By profession" rows={data.byProfession} /><BreakdownTable title="By country" rows={data.byCountry} /></Stack>
                    </>
                ) : (
                    <Box><Button variant="outlined" startIcon={<RefreshOutlinedIcon />} onClick={() => void load()}>Retry</Button></Box>
                )}
            </Stack>
        </Container>
    );
}
